// Admin radar data layer (admin.developerz.ai). Reads under `/v1/admin/*`
// (gated `admin:read:*` on the server); one round-trip each over `apiFetch`
// reusing the staff SSO session. Mirrors `lib/fleet.ts`: `token` defaults to
// `SESSION_TOKEN`, queries stay idle (no fetch) with no session.
//
// WRITES ARE REST TWINS. The user registry's approve / suspend live here, as
// `POST /v1/admin/users/:id/{approve,suspend}` — the REST twins of the MCP
// `user approve` / `user suspend` actions (`packages/mcp/src/army/user.ts`;
// arc 101 slice 01, #3703 pattern: one control behind both doors). They used
// to ride that same `user` verb over JSON-RPC from this SPA
// (the deleted `lib/adminMcp.ts`), a second transport for the same act; the
// operator overrides followed into `lib/admin-overrides.ts` (plan 101 row 17-1). `admin:users` (and `admin:goals`,
// `admin:accounts` for the goal rename and the staff role change) is an
// operator-credential scope no session carries, so every write here runs under
// `withOperatorCredential` (`./operator-credential.ts`, #5724). Sibling resources own their own module (`adminOss.ts`,
// `adminGrants.ts`, `admin-access-requests.ts`, `admin-invitations.ts`).

import {
  ADMIN_BILLING_FILTERS,
  type AdminBillingFilter,
  type Role,
  type TaskRunState,
} from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { t } from '../i18n';
import type { ProvisionGrant } from './admin-provision';
import type { AdminGrant } from './adminGrants';
import { ApiError, apiFetch, UserFacingError } from './api';
import { type OperatorOutcome, withOperatorCredential } from './operator-credential';
import type { Page } from './paged';
import { SESSION_TOKEN } from './session';

/** The canonical cursor-paginated slice — declared once, in `lib/paged`. */
export type { Page };

/** One day of finished-session outcome counts in the reliability window. */
export type AdminReliabilityDay = {
  readonly day: string;
  readonly total: number;
  readonly completed: number;
  readonly escalated: number;
  readonly failed: number;
};

/** One model's finished-session outcome split over the window. */
export type AdminReliabilityModel = {
  readonly model: string;
  readonly total: number;
  readonly completed: number;
  readonly escalated: number;
  readonly failed: number;
};

/** One unmet GA-readiness check (#761) — observed vs required, so the radar can say WHY. */
export type AdminGateFailure = {
  readonly code: 'clean_terminal_below' | 'failed_above' | 'sample_too_small' | 'too_few_models';
  readonly observed: number;
  readonly required: number;
};

/**
 * The #761 escalation-rate figure — finished-session outcome rates over a trailing
 * window, so the escalation rate reads off the radar instead of a manual log
 * count. `escalationRate`/`cleanTerminalRate`/`failedRate` are ratios in `[0, 1]`
 * (0 when the window is empty). `gaGate` is the DARK GA-readiness verdict — a
 * machine GO/NO-GO that flips nothing live.
 */
export type AdminReliability = {
  readonly windowDays: number;
  readonly sampleSize: number;
  readonly escalationRate: number;
  readonly cleanTerminalRate: number;
  readonly failedRate: number;
  readonly byDay: readonly AdminReliabilityDay[];
  readonly byModel: readonly AdminReliabilityModel[];
  readonly escalationCauses: readonly { readonly category: string; readonly n: number }[];
  readonly gaGate: {
    readonly pass: boolean;
    readonly failures: readonly AdminGateFailure[];
  };
};

/** Platform-wide counters — `GET /v1/admin/overview`. */
export type AdminOverview = {
  readonly accounts: { readonly total: number; readonly by_tier: Readonly<Record<string, number>> };
  readonly users: {
    readonly total: number;
    readonly approved: number;
    readonly waitlisted: number;
  };
  readonly runs: { readonly total: number; readonly last_24h: number };
  readonly fleet: {
    readonly total: number;
    readonly online: number;
    readonly offline: number;
    readonly pending: number;
    readonly revoked: number;
  };
  readonly queue: {
    readonly depth: number;
    readonly pending: number;
    readonly assigned: number;
    readonly in_progress: number;
    readonly failed: number;
  };
  readonly spend: { readonly mtd_usd: string };
  /**
   * Who pays and who is comped (#5726). `paying` = `seats > 0 OR purchased_ci_slots > 0`.
   * `comped_accounts` = accounts lifted by a LIVE `ai_devs` grant (an account grant, or
   * the owner's grant on a personal account); `comped_ai_devs` = the seats it lifts.
   * No revenue figure by decision:
   * Stripe is not live (#2771), and a list-price estimate would be wrong unnoticed.
   */
  readonly billing: {
    readonly paying_accounts: number;
    readonly paid_seats: number;
    readonly purchased_ci_slots: number;
    readonly paused_accounts: number;
    readonly comped_ai_devs: number;
    readonly comped_accounts: number;
  };
  readonly reliability: AdminReliability;
  /**
   * The radar's 30-day history (#6715): metric (`accounts.total`, `accounts.tier.<tier>`,
   * `users.total|approved|waitlisted`, `runs.total|last_24h`) -> one point per UTC day
   * (`'YYYY-MM-DD'`), day ascending, today-30 … today. A day with no snapshot is ABSENT,
   * never zero; a tier starts at its first snapshot. `lib/radar-trends.ts` reads it.
   */
  readonly trends: Readonly<
    Record<string, readonly { readonly day: string; readonly value: number }[]>
  >;
};

/**
 * The org list's `?billing=` value, or `null` for none AND for one outside
 * `ADMIN_BILLING_FILTERS`. An unknown value is dropped here rather than forwarded:
 * the API 400s it, and an error page is a worse answer to a stale link than the
 * whole list with the filter control reading "Any billing".
 */
export function billingFilterFrom(raw: unknown): AdminBillingFilter | null {
  return ADMIN_BILLING_FILTERS.find((f) => f === raw) ?? null;
}

/** The org list narrowed to one billing state — the overview's drill-in target. */
export function adminOrgsHref(billing: AdminBillingFilter): string {
  return `/admin/orgs?billing=${billing}`;
}

/** One row of the global audit tail — `GET /v1/admin/audit`. */
export type AdminAuditRow = {
  readonly id: string;
  readonly ts_iso: string;
  readonly kind: string;
  readonly tool: string | null;
  readonly outcome: string | null;
  readonly session_id: string | null;
  readonly account_login: string | null;
};

/** One account row — `GET /v1/admin/accounts`. */
export type AdminAccountRow = {
  readonly id: string;
  readonly github_login: string;
  readonly kind: string;
  readonly tier: string;
  readonly seats: number;
  /** `null` = no cap set (the API serializes `spendCapUsd: string | null`). */
  readonly spend_cap_usd: string | null;
  readonly billing_paused_at_iso: string | null;
  readonly oss_verified: boolean;
  readonly created_at_iso: string;
  readonly members_count: number;
};

/** One org member — nested in the account detail. */
export type AdminAccountMember = {
  readonly user_id: string;
  readonly login: string;
  readonly avatar: string | null;
  readonly role: string;
};

/**
 * Account detail — the row plus its members.
 *
 * The wire still carries a `scout_survey` block (the MCP `account_detail` verb projects
 * it), and this app deliberately does not declare it: `AdminScoutSurveyBand` folded into
 * the platform-wide `AdminHiveBand`, which grades all five fan-out lanes rather than one
 * lane for one tenant. A mirror type with no reader is the next stale fixture.
 */
export type AdminAccountDetail = AdminAccountRow & {
  readonly members: readonly AdminAccountMember[];
  /**
   * Non-null = a Stripe seat subscription owns the tier and seats: their
   * overrides answer 409 (#5725), so the org page shows both read-only (#6425).
   */
  readonly stripe_seat_subscription_id: string | null;
};

/** One membership a user holds — nested in the user row. */
export type AdminUserMembership = {
  readonly account_id: string;
  readonly github_login: string;
  readonly role: string;
};

/** One row of the user registry — `GET /v1/admin/users`. */
export type AdminUserRow = {
  readonly id: string;
  readonly login: string;
  readonly email: string | null;
  readonly avatar: string | null;
  readonly approved_at_iso: string | null;
  readonly created_at_iso: string;
  readonly memberships: readonly AdminUserMembership[];
};

/** One membership on the per-user read: the registry's, plus the account's kind. */
export type AdminUserDetailMembership = AdminUserMembership & {
  readonly kind: string;
};

/**
 * One person across every org — `GET /v1/admin/users/:id` (#5727). The server
 * appends an `admin.tenant_read` platform-audit row before it answers, since the
 * memberships disclose tenant data. `last_active_iso` is `null` when nothing was
 * ever recorded. `grants` are the grants whose subject is this user.
 */
export type AdminUserDetail = Omit<AdminUserRow, 'memberships'> & {
  readonly last_active_iso: string | null;
  readonly memberships: readonly AdminUserDetailMembership[];
  readonly grants: readonly AdminGrant[];
};

/**
 * Registry status filter — the vocabulary of the API's `?filter=status:<value>`
 * term. `all` is the ABSENCE of the fence, so this client sends no term for it.
 */
export type AdminUserStatus = 'all' | 'approved' | 'waitlisted';

/** One run's pulse — the same block `GET /v1/tasks/:id` carries as `run`. */
export type AdminRunPulse = {
  readonly run_id: string;
  readonly last_seq: number | null;
  readonly last_kind: string | null;
  readonly last_at: string | null;
  /** `state === 'finished'` only. `false` is NOT live: render `state` (#5918). */
  readonly finished: boolean;
  /** Where the run is — decided server-side, the one field the pill renders. */
  readonly state: TaskRunState;
};

/**
 * One run, read ACROSS tenants — `GET /v1/admin/fleet/runs/:runId`.
 *
 * `account_id` / `account_login` are the point: an ordinary run read is fenced to
 * the caller's own account, and the operator opening this page usually is not a
 * member of the org that owns the run. The server resolves the owner and says who
 * it is, and records the read in the platform audit trail before answering.
 */
export type AdminRun = {
  readonly run_id: string;
  readonly account_id: string;
  readonly account_login: string;
  readonly task_id: string | null;
  readonly run: AdminRunPulse;
};

/**
 * ONE STAT ON THE HIVE BAND, and it always carries its denominator — `GET
 * /v1/admin/hive`. Mirrors the API's `AdminHiveStat` on the wire's own snake_case.
 *
 * A DISCRIMINATED verdict rather than a bare number, because the three things a rate can
 * be render three different ways and collapsing any two into `0%` is the defect this band
 * exists to end:
 *
 *   - `graded` — measured, `n` of `d`.
 *   - `no_denominator` — nothing to measure in this window. Renders as "no data", never
 *     as `0%`: `usage` at 901 rows against 20,727 `agent_response` is the standing
 *     reminder of what a rate over an unmeasured denominator costs.
 *   - `not_on_contract` — this lane's payload has no such field. `attempts` rides
 *     `team_fanout` alone.
 */
export type AdminHiveStat =
  | { readonly kind: 'graded'; readonly rate: number; readonly n: number; readonly d: number }
  | { readonly kind: 'no_denominator'; readonly n: number; readonly d: 0 }
  | { readonly kind: 'not_on_contract' };

/**
 * A named dead-producer verdict on one lane — the mechanism ran and the observable hop
 * reported nothing. Graded SERVER-SIDE so the dashboard and `bun run infra fleet-health`
 * cannot disagree about whether a lane is alerting.
 */
export type AdminHiveAlert =
  | { readonly kind: 'steerable_dead'; readonly dispatched: number }
  | { readonly kind: 'steered_dead'; readonly steerable: number }
  | { readonly kind: 'no_row_parsed'; readonly events: number };

/** One parse-failure signature: which keys the stored row could not satisfy, and when. */
export type AdminHiveParseFailure = {
  readonly reason: string;
  readonly events: number;
  readonly last_at_iso: string;
};

/**
 * Which binary WROTE a bucket's rows: the run's own `run_provenance.runner_version`
 * (`stamped`), a run that stamped before that key existed (`pre_stamp`), or a run whose
 * lane never emits provenance (`no_provenance` — every hive lane today). Never the box's
 * version of today, which named the build that REPLACED the writer.
 */
export type AdminHiveWriter =
  | { readonly kind: 'stamped'; readonly version: string }
  | { readonly kind: 'pre_stamp' }
  | { readonly kind: 'no_provenance' };

/**
 * The refused rows of one window, grouped by the binary that wrote them. On the two
 * unstamped kinds the bucket is only actionable together with `reasons[].last_at_iso`,
 * which is what tells dead history from a live regression.
 */
export type AdminHiveVersionBucket = {
  readonly writer: AdminHiveWriter;
  readonly events: number;
  readonly parsed: number;
  readonly unparseable: number;
  readonly reasons: readonly AdminHiveParseFailure[];
  readonly reasons_truncated: number;
};

/** One fan-out lane's hive health over the trailing window. */
export type AdminHiveLane = {
  readonly lane: string;
  readonly events: number;
  readonly parsed: number;
  readonly unparseable: number;
  readonly unparseable_rate: number;
  readonly last_at_iso: string | null;
  readonly rounds: number;
  readonly multi_member_rate: AdminHiveStat;
  readonly idle_rate: AdminHiveStat;
  readonly steerable_rate: AdminHiveStat;
  readonly steered_rate: AdminHiveStat;
  readonly falsified_rate: AdminHiveStat;
  readonly no_work_rate: AdminHiveStat;
  readonly unrouted_handoff_rate: AdminHiveStat;
  readonly attempts_rate: AdminHiveStat;
  readonly denominators: {
    readonly events: number;
    readonly parsed: number;
    readonly rounds: number;
    readonly dispatched: number;
    readonly steerable: number;
    readonly round_ms: number;
    readonly handoffs: number;
  };
  readonly alerts: readonly AdminHiveAlert[];
  readonly by_version: readonly AdminHiveVersionBucket[];
};

/**
 * The hive's rates across every fan-out lane — `GET /v1/admin/hive`, the reader
 * `docs/idea/hive.md` asks for ("the `no_answer` and `prose` rates belong on a
 * dashboard"), widened from the scout lane to all five.
 */
export type AdminHive = {
  readonly window_days: number;
  readonly lanes: readonly AdminHiveLane[];
};

export const ADMIN_OVERVIEW_KEY = 'admin_overview';
export const ADMIN_HIVE_KEY = 'admin_hive';
export const ADMIN_RUN_KEY = 'admin_run';
export const ADMIN_AUDIT_KEY = 'admin_audit';
export const ADMIN_ACCOUNTS_KEY = 'admin_accounts';
export const ADMIN_ACCOUNT_KEY = 'admin_account';
export const ADMIN_USERS_KEY = 'admin_users';
export const ADMIN_USER_KEY = 'admin_user';

/** Platform counters. Idle (no fetch) with no session. */
export function useAdminOverview(token: string | null = SESSION_TOKEN) {
  return createQuery<AdminOverview>(() => ({
    queryKey: [ADMIN_OVERVIEW_KEY, token],
    enabled: token !== null,
    queryFn: () => apiFetch<AdminOverview>('/v1/admin/overview', token),
  }));
}

/**
 * The hive's rates, every fan-out lane (`GET /v1/admin/hive`). Idle with no session.
 *
 * No parameters: the window is the server's, so the band, the operator's terminal and
 * this page are all reading one number. Deliberately NOT polled — a 7-day rollup does
 * not move between two glances at it, and the fleet converges by pull-before-work with a
 * ~75-minute lag, so a refetch interval would spend a whole-window jsonb read to render
 * the same figure.
 */
export function useAdminHive(token: string | null = SESSION_TOKEN) {
  return createQuery<AdminHive>(() => ({
    queryKey: [ADMIN_HIVE_KEY, token],
    enabled: token !== null,
    queryFn: () => apiFetch<AdminHive>('/v1/admin/hive', token),
  }));
}

/**
 * ONE page of the global audit tail, newest-first, at `cursor`. Keyset-paged
 * server-side, so hand it to `usePagedQuery`: the cursor accessor is part of the
 * query key and the walk refetches itself. It reads a real `next_cursor` — the
 * tail is a window on a table that only grows, never a complete collection.
 *
 * `per_page` is the parameter, and the `?limit` alias that used to sit beside it
 * is gone — the route now 400s it by name, so this client sending `per_page` is
 * the only thing that works.
 */
export function useAdminAudit(
  cursor: () => string | null,
  perPage = 50,
  token: string | null = SESSION_TOKEN,
) {
  return createQuery<Page<AdminAuditRow>>(() => ({
    queryKey: [ADMIN_AUDIT_KEY, token, cursor(), perPage],
    enabled: token !== null,
    queryFn: () =>
      apiFetch<Page<AdminAuditRow>>(`/v1/admin/audit?${listParams(cursor(), perPage)}`, token),
  }));
}

/**
 * One run, whoever owns it. Idle until `runId` is non-null.
 *
 * Deliberately NOT org-scoped: the route this backs lives at the TOP level
 * (`/admin/runs/:run_id`), so `orgFromPath` resolves nothing and `apiFetch` sends
 * no `X-Dz-Account` header — which is exactly what lets the server resolve the
 * owner itself. Sending one would re-fence the read to whichever org the URL
 * named, which is the bug this page exists to fix.
 */
export function useAdminRun(runId: () => string | null, token: string | null = SESSION_TOKEN) {
  return createQuery<AdminRun>(() => ({
    queryKey: [ADMIN_RUN_KEY, token, runId()],
    enabled: token !== null && runId() !== null,
    queryFn: () =>
      apiFetch<AdminRun>(`/v1/admin/fleet/runs/${encodeURIComponent(runId() as string)}`, token),
  }));
}

/** One page of accounts, filtered by `billing` + `q` and positioned by `cursor`. */
export function useAdminAccounts(
  billing: () => AdminBillingFilter | null,
  q: () => string,
  cursor: () => string | null,
  perPage = 30,
  token: string | null = SESSION_TOKEN,
) {
  return createQuery<Page<AdminAccountRow>>(() => ({
    queryKey: [ADMIN_ACCOUNTS_KEY, token, billing(), q(), cursor(), perPage],
    enabled: token !== null,
    queryFn: () =>
      apiFetch<Page<AdminAccountRow>>(
        `/v1/admin/accounts?${listParams(cursor(), perPage, { q: q(), billing: billing() })}`,
        token,
      ),
  }));
}

/** One account's detail + members. Idle until `id` is non-null. */
export function useAdminAccount(id: () => string | null, token: string | null = SESSION_TOKEN) {
  return createQuery<AdminAccountDetail>(() => ({
    queryKey: [ADMIN_ACCOUNT_KEY, token, id()],
    enabled: token !== null && id() !== null,
    queryFn: () =>
      apiFetch<AdminAccountDetail>(
        `/v1/admin/accounts/${encodeURIComponent(id() as string)}`,
        token,
      ),
  }));
}

/** One person's page — identity, memberships, grants. Idle until `id` is non-null. */
export function useAdminUser(id: () => string | null, token: string | null = SESSION_TOKEN) {
  return createQuery<AdminUserDetail>(() => ({
    queryKey: [ADMIN_USER_KEY, token, id()],
    enabled: token !== null && id() !== null,
    queryFn: () =>
      apiFetch<AdminUserDetail>(`/v1/admin/users/${encodeURIComponent(id() as string)}`, token),
  }));
}

/**
 * The org list with one org's detail open — where a membership links to, and
 * what a registry row links to, carrying the list's billing filter along.
 */
export function adminOrgHref(accountId: string, billing: AdminBillingFilter | null = null): string {
  const params = new URLSearchParams(billing === null ? {} : { billing });
  params.set('account', accountId);
  return `/admin/orgs?${params.toString()}`;
}

/**
 * What an approve did (plan 102 slice 05). FUNDING PRECEDES APPROVAL server-side.
 * `approved` = the 200: the person is approved and `grants` is what they now hold —
 * the invite bundle just granted, or the live grants they already had.
 * `not_approved` = the 422 ("not approved: the bundle was refused (<code>): …"): the
 * bundle was refused, so the person was NOT approved and stays waitlisted; `detail`
 * is the server's sentence, shown verbatim as the row's error.
 */
export type AdminUserApproval =
  | {
      readonly kind: 'approved';
      readonly grants: readonly ProvisionGrant[];
      /** The server's box budget (`null` = uncapped); `undefined` when the answer named none. */
      readonly boxes: number | null | undefined;
    }
  | { readonly kind: 'not_approved'; readonly detail: string };

/**
 * Approve one user off the closed-beta waitlist — stamps `users.approved_at` and,
 * when they hold no live capacity grant, grants the invite bundle. So the route
 * needs BOTH `admin:grants` (its scope) and `admin:users` (its guard).
 * Idempotent server-side; the caller re-reads the registry rather than
 * patching the row.
 */
export function approveAdminUser(
  id: string,
  token: string | null = SESSION_TOKEN,
): Promise<OperatorOutcome<AdminUserApproval>> {
  return withOperatorCredential(
    {
      scopes: ['admin:users', 'admin:grants'],
      name: 'admin console: approve a user',
      token,
    },
    async (send) => {
      try {
        const body = await send<{ grants?: readonly ProvisionGrant[]; boxes?: number | null }>(
          `/v1/admin/users/${encodeURIComponent(id)}/approve`,
          { method: 'POST' },
        );
        return { kind: 'approved', grants: body?.grants ?? [], boxes: body?.boxes };
      } catch (err) {
        if (err instanceof ApiError && err.status === 422) {
          return { kind: 'not_approved', detail: err.detail ?? err.message };
        }
        throw err;
      }
    },
  );
}

/** Suspend one user — clears `approved_at`, capping every session to `read:account`. */
export function suspendAdminUser(
  id: string,
  token: string | null = SESSION_TOKEN,
): Promise<OperatorOutcome<void>> {
  return userWrite(id, 'suspend', token);
}

function userWrite(
  id: string,
  verb: 'suspend',
  token: string | null,
): Promise<OperatorOutcome<void>> {
  return withOperatorCredential(
    { scopes: ['admin:users'], name: `admin console: ${verb} a user`, token },
    (send) => send<void>(`/v1/admin/users/${encodeURIComponent(id)}/${verb}`, { method: 'POST' }),
  );
}

/** `PATCH /v1/admin/accounts/:id/members/:userId`' answer. */
export type AdminMemberRoleChanged = {
  readonly account_id: string;
  readonly user_id: string;
  readonly role: Role;
  readonly previous_role: Role;
};

/**
 * Set one member's role in any org, as staff (#5727) — `admin:accounts`, under
 * an operator credential. The server keeps every org's last owner: demoting it
 * is a 409 `reason: last_owner`, rethrown here as a localized `UserFacingError`
 * so the control says why in the reader's language. The caller re-reads.
 */
export function setAdminMemberRole(
  accountId: string,
  userId: string,
  role: Role,
  token: string | null = SESSION_TOKEN,
): Promise<OperatorOutcome<AdminMemberRoleChanged>> {
  const path = `/v1/admin/accounts/${encodeURIComponent(accountId)}/members/${encodeURIComponent(userId)}`;
  return withOperatorCredential(
    { scopes: ['admin:accounts'], name: 'admin console: change a member role', token },
    async (send) => {
      try {
        return await send<AdminMemberRoleChanged>(path, {
          method: 'PATCH',
          body: JSON.stringify({ role }),
        });
      } catch (err) {
        if (err instanceof ApiError && err.extensions?.['reason'] === 'last_owner') {
          throw new UserFacingError(t('admin.member_role_last_owner'));
        }
        throw err;
      }
    },
  );
}

/** One page of the user registry, filtered by `status` + `q`. */
export function useAdminUsers(
  status: () => AdminUserStatus,
  q: () => string,
  cursor: () => string | null,
  perPage = 30,
  token: string | null = SESSION_TOKEN,
) {
  return createQuery<Page<AdminUserRow>>(() => ({
    queryKey: [ADMIN_USERS_KEY, token, status(), q(), cursor(), perPage],
    enabled: token !== null,
    queryFn: () =>
      apiFetch<Page<AdminUserRow>>(
        `/v1/admin/users?${listParams(cursor(), perPage, { q: q(), status: status() })}`,
        token,
      ),
  }));
}

/**
 * The ONE query builder for every admin collection: the shared list grammar's
 * `per_page`/`cursor`/`q`, plus `status` as the `?filter=status:<value>` term it
 * became when these registries came off their private grammar, and the org
 * list's `billing` as `?filter=billing:<value>` (#5726). No collection takes both.
 *
 * One builder because every collection here answers to ONE page-size word.
 * `?limit` was its deprecated alias and is gone (the route 400s it by name), so
 * two builders would now be two spellings only one of which is served.
 *
 * `all` sends NO term. On the user registry that is the same answer either way;
 * on the OSS queue it is not, which is why that route's caller passes `all`
 * explicitly rather than omitting the argument — see `lib/adminOss.ts`.
 */
function listParams(
  cursor: string | null,
  perPage: number,
  filters: { q?: string; status?: string; billing?: AdminBillingFilter | null } = {},
): string {
  const params = new URLSearchParams({ per_page: String(perPage) });
  if (cursor) params.set('cursor', cursor);
  if (filters.q) params.set('q', filters.q);
  if (filters.status && filters.status !== 'all') {
    params.set('filter', `status:${filters.status}`);
  }
  if (filters.billing) params.set('filter', `billing:${filters.billing}`);
  return params.toString();
}

// ── the autonomous goal monitor (`GET /v1/admin/goals`) ───────────────────────

/** One goal, as the API grades it — mirrors `@developerz/domain` `Goal`. */
export type AdminGoal = {
  kind: 'milestone' | 'epic';
  id: string;
  title: string;
  url: string;
  status: 'not_started' | 'in_progress' | 'stalled' | 'blocked' | 'finished';
  percent: number;
  /** False for a one-item goal (every epic) — show no progress bar for those. */
  measurable: boolean;
  open: number;
  closed: number;
  total: number;
  blocked: number;
  daysSinceProgress: number | null;
  lastClosedAt: string | null;
  dueOn: string | null;
  overdue: boolean;
};

export type AdminGoalBoard = {
  repo: string;
  observedAt: string;
  summary: {
    goals: number;
    finished: number;
    inProgress: number;
    stalled: number;
    blocked: number;
    notStarted: number;
    openItems: number;
    waitingOnYou: number;
    percent: number;
  };
  goals: AdminGoal[];
};

export const ADMIN_GOALS_KEY = 'admin-goals';

/**
 * How often an OPEN goal board re-reads GitHub.
 *
 * Deliberately slower than the 30s the fleet and overview reads use, because one
 * board read is not one request: the API fetches the repo's milestones and then
 * walks its issues in bounded pages. Measured against this repo on 2026-08-17
 * that was eleven GitHub calls per read, spent from an installation budget of
 * 5 000/hour that the maintainer bot's own triage, PR and merge loops draw on
 * too. At 30s one forgotten tab would take a quarter of that budget to watch
 * counts which move a handful of times a day; five minutes takes a fortieth.
 *
 * The number above is a measurement, not an invariant — the bound it came from
 * lives with the scan in `@developerz/github`, which this app cannot import.
 * Widen that walk and this interval wants revisiting; nothing here will notice.
 *
 * TanStack does not poll a backgrounded tab (`refetchIntervalInBackground`
 * defaults false), so this is the cost of a board someone is LOOKING at. A tab
 * left in the background therefore shows a STALE board on return until the next
 * tick — which is why the page prints the timestamp it was read at.
 *
 * ponytail: an interval, not a live feed. If waiting five minutes after a merge
 * ever grates, the next step is a refresh button — not a shorter interval, which
 * spends the bot's rate limit on an answer nobody is reading.
 */
export const GOAL_REFRESH_MS = 5 * 60_000;

/**
 * The platform's own goal board.
 *
 * A 503 here is MEANINGFUL, not a transport blip: the API answers one when the
 * board cannot be read (no App credentials on the pod, the platform repo has no
 * live installation, GitHub is down) precisely so the page can say WHICH — an
 * empty board would read as "we have no goals". `QueryBoundary` surfaces the
 * error rather than an empty state for exactly that reason.
 */
/**
 * Rename one goal — a write THROUGH to GitHub, not a local label.
 *
 * Returns the title GITHUB stored, which is not always the one sent: it trims,
 * and it caps length. The caller shows THAT, so a rename never reports a change
 * the server did not make.
 *
 * The board is deliberately re-fetched by the caller rather than patched in
 * place here: the page's claim is that every number on it came from GitHub, and
 * a locally-spliced title would be the one field on the board that did not.
 */
export function renameAdminGoal(
  id: string,
  title: string,
  token: string | null = SESSION_TOKEN,
): Promise<OperatorOutcome<{ id: string; title: string }>> {
  return withOperatorCredential(
    { scopes: ['admin:goals'], name: 'admin console: rename a roadmap goal', token },
    (send) =>
      send<{ id: string; title: string }>(`/v1/admin/goals/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ title }),
      }),
  );
}

export function useAdminGoals(token: string | null = SESSION_TOKEN) {
  return createQuery<AdminGoalBoard>(() => ({
    queryKey: [ADMIN_GOALS_KEY, token],
    enabled: token !== null,
    queryFn: () => apiFetch<AdminGoalBoard>('/v1/admin/goals', token),
    refetchInterval: GOAL_REFRESH_MS,
  }));
}
